import { useMemo } from "react";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ExternalBillingClaimStatus } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimStatus";
import { PopulationBasedFundingEventDetails } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingEventDetails";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingEventAction } from "@/Common/Models/UI/Enums";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import styles from "./PopulationBasedFundingCollapsibleEventDetails.module.scss";

interface PopulationBasedFundingCollapsibleEventDetailsProps {
    isCollapsed: boolean;
    eventModel: PopulationBasedFundingEventWithNotesModel;
    onCollapseToggle: () => void;
    className?: string;
    onDeleteClick?: () => void;
    onEditClick?: () => void;
    onAcceptClick?: () => void;
    onOverrideClick?: () => void;
    onCancelInternalProposalClick?: () => void;
    onRegisterFromInternalProposalClick?: () => void;
    disableActions: boolean;
}

function PopulationBasedFundingCollapsibleEventDetails(props: PopulationBasedFundingCollapsibleEventDetailsProps): JSX.Element {
    const { getText } = useTranslation();

    const { onAcceptClick, onOverrideClick, onEditClick, onDeleteClick, onCancelInternalProposalClick, onRegisterFromInternalProposalClick, eventModel, disableActions } = props;
    const { isProposal, isPendingProposal, IsLatestInPatientHistory, isPendingInternalProposal, isAcceptedInternalProposal, ClaimStatus, EmployeeContactFacetId } = eventModel;

    const isAcceptedRegistrationProposalWithUnknownClinician = !EmployeeContactFacetId
        && isProposal
        && eventModel.ActionEnum === PopulationBasedFundingEventAction.Register
        && !!eventModel.AcceptedDate;

    const renderActionLinks = useMemo(
        (): JSX.Element[] => {
            if (disableActions
                || ClaimStatus === ExternalBillingClaimStatus.Refused
                || ClaimStatus === ExternalBillingClaimStatus.Rejected
            )
                return null;

            const actions: JSX.Element[] = [];

            if (isPendingInternalProposal) {
                if (onRegisterFromInternalProposalClick)
                    actions.push(<a onClick={() => onRegisterFromInternalProposalClick()} key="registerLink">{getText("RegisterLabel")}</a>);

                if (onCancelInternalProposalClick)
                    actions.push(<a onClick={() => onCancelInternalProposalClick()} key="cancelLink">{getText("CancelLabel")}</a>);
            }
            else {
                if (((!isProposal || isAcceptedInternalProposal) || isAcceptedRegistrationProposalWithUnknownClinician) && onEditClick)
                    actions.push(<a onClick={() => onEditClick()} key="editLink">{getText("EDITLabel")}</a>);

                if (!isProposal && IsLatestInPatientHistory && onDeleteClick)
                    actions.push(<a onClick={() => onDeleteClick()} key="deleteLink">{getText("DeleteLabel")}</a>);

                if (isPendingProposal && !!onAcceptClick)
                    actions.push(<a onClick={() => onAcceptClick()} key="acceptLink">{getText("AcceptLabel")}</a>);

                if (isPendingProposal && !!onOverrideClick)
                    actions.push(<a onClick={() => onOverrideClick()} key="overrideLink">{getText("OverrideLabel")}</a>);
            }

            return actions.map((action, index) => (index > 0) ? <span key={`separatorr_${index}`}><span key={`separator_${index}`}> | </span>{action}</span> : action);
        },
        [
            disableActions,
            isPendingInternalProposal,
            onRegisterFromInternalProposalClick,
            getText,
            onCancelInternalProposalClick,
            isProposal,
            onEditClick,
            IsLatestInPatientHistory,
            onDeleteClick,
            isPendingProposal,
            onAcceptClick,
            onOverrideClick,
            ClaimStatus,
            isAcceptedInternalProposal,
            isAcceptedRegistrationProposalWithUnknownClinician
        ]
    );

    return (
        <div className={`${styles.populationBasedFundingPatientHistoryItem} ${(props.className ?? "")}`}>
            <div className={`${styles.flexContainer} ${styles.collapsibleHeader}`} onClick={() => props.onCollapseToggle()}>
                <div className={styles.flexContainer}>
                    <div className={styles.flexContainer}>
                        <div className={styles.actionContainer}>{PopulationBasedFundingHelper.getEventActionText(props.eventModel)}</div>
                        <div><strong>{LuxonDateTimeParser.parse(props.eventModel.EffectiveDate).toFormat("yyyy/LL/dd")}</strong></div>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <span>{props.isCollapsed ? <span>&#x25BE;</span> : <span>&#x25B4;</span>}</span>
                </div>
            </div>
            {!props.isCollapsed && (
                <div className={styles.flexContainer}>
                    <div className={`${styles.patientHistoryItemCollapsibleBody} ${styles.flexContainer}`}>
                        <PopulationBasedFundingEventDetails
                            physicianName={Employee.getFullNameWithPrefixContactFacet(props.eventModel.EmployeeContactFacetId)}
                            effectiveDate={props.eventModel.EffectiveDate}
                            proposalDate={props.eventModel.ProposalDate}
                            proposalReasonVocabKey={props.eventModel.IsInternalProposal
                                ? "InternalProposalLabel"
                                : PopulationBasedFundingHelper.getProposalReasonVocabKey(props.eventModel.ProposalReason)
                            }
                            genericReason={props.eventModel.IsPatientTransfered ? "PatientTransferedLabel" : null}
                            deregisteredReasonVocabKey={PopulationBasedFundingHelper.getDeregisterReasonVocabKey(props.eventModel.DeregisteredReasonEnum)}
                            overrideDate={props.eventModel.OverrideDate}
                            overrideReasonVocabKey={PopulationBasedFundingHelper.getOverrideReasonVocabKey(props.eventModel.OverrideReasonEnum)}
                            deregistrationOrProposalCustomReason={props.eventModel.DeregistrationOrProposalCustomReason}
                            notes={props.eventModel.Notes.any()
                                ? props.eventModel.Notes
                                    .map(note => `${cacheStore.GetEmployeeByUserId(String(note.CreatedBy)).UserName} (${LuxonDateTimeParser.parse(note.CreatedDate).toJSDate().format("YYYY/MM/DD  hh:mm:ss A")}) ${note.Content}`)
                                : []
                            }
                        />
                        <div className={styles.actions}>
                            {renderActionLinks}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export { PopulationBasedFundingCollapsibleEventDetails };
